import React, { useState, useEffect } from 'react';
import { generateChatMessage, getAvailableModels } from '../services/openRouterService';
import SearchableSelect from './SearchableSelect';
import { exportWorkPlans, downloadExportFile, readImportFile, importWorkPlans } from '../utils/workPlanExport';
import './WorkPlan.css';

const WorkPlan = () => {
  // State for different views
  const [currentView, setCurrentView] = useState('dashboard');
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showContentPlanModal, setShowContentPlanModal] = useState(false);
  const [showContentPlanImportModal, setShowContentPlanImportModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [contentPlanImportText, setContentPlanImportText] = useState('');
  const [contentPlanImportPlatform, setContentPlanImportPlatform] = useState('facebook');
  const [contentPlanImportStartDate, setContentPlanImportStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [contentPlanImportCampaignName, setContentPlanImportCampaignName] = useState('');
  const [selectedPlansForExport, setSelectedPlansForExport] = useState([]);
  const [includeTemplates, setIncludeTemplates] = useState(false);
  const [taskFilter, setTaskFilter] = useState('all');
  const [taskViewFilter, setTaskViewFilter] = useState(null); // 'pending', 'completed', 'overdue', 'all-plans', 'active-plans'
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [contentPlanData, setContentPlanData] = useState({
    days: [],
    platform: 'instagram',
    campaignName: '',
    description: '',
    duration: 30
  });

  // Connection and error handling states
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const [retryCount, setRetryCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [offlineData, setOfflineData] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');



  // Task states
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'medium',
    status: 'pending',
    type: 'content-creation',
    // New content planning fields
    theme: '',
    contentType: 'carousel',
    platform: 'instagram',
    instructions: '',
    hashtags: '',
    estimatedTime: ''
  });

  // New task input system
  const [taskInputText, setTaskInputText] = useState('');
  const [parsedTasks, setParsedTasks] = useState([]);
  const [showTaskParser, setShowTaskParser] = useState(false);

  // User saved templates
  const [userTemplates, setUserTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    category: 'social-media'
  });
  
  // AI Content Generation State
  const [selectedModel, setSelectedModel] = useState('deepseek/deepseek-chat-v3.1:free'); // Default to free model
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingTaskId, setGeneratingTaskId] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  // Collapsible generated content per task (taskId -> expanded)
  const [expandedTasks, setExpandedTasks] = useState({});
  // Track which generated images failed to load (e.g. broken/expired URLs from AI)
  const [failedGeneratedImages, setFailedGeneratedImages] = useState({});

  // Load available models on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        const apiKey = localStorage.getItem('openrouter_api_key');
        const models = await getAvailableModels(false, apiKey);
        setAvailableModels(models);
        
        // Load default model from settings
        const savedDefaultModel = localStorage.getItem('default_ai_model');
        if (savedDefaultModel && models.find(m => m.id === savedDefaultModel && m.isAvailable !== false)) {
          setSelectedModel(savedDefaultModel);
        } else {
        // If selected model is unavailable or paid, switch to first available free model
        const selectedModelObj = models.find(m => m.id === selectedModel);
        if (!selectedModelObj || selectedModelObj.isAvailable === false || !selectedModelObj.isFree) {
          const firstFreeAvailable = models.find(m => m.isAvailable !== false && m.isFree === true);
          if (firstFreeAvailable) {
            setSelectedModel(firstFreeAvailable.id);
          } else {
            // Fallback to first available model if no free models found
            const firstAvailable = models.find(m => m.isAvailable !== false);
            if (firstAvailable) {
              setSelectedModel(firstAvailable.id);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading models:', error);
        // Fallback to synchronous getAvailableModels
        setAvailableModels(getAvailableModels());
      }
    };
    
    loadModels();
  }, []);

  // Markdown to HTML converter (same as in ScriptEditor)
  const markdownToHtml = (markdown) => {
    if (!markdown) return '';
    const escapeHtml = (str) => String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    let html = markdown.replace(/```([\s\S]*?)```/g, (m, code) => {
      return `<pre><code>${escapeHtml(code.trim())}</code></pre>`;
    });
    html = html.replace(/^######\s?(.*)$/gm, '<h6>$1</h6>')
               .replace(/^#####\s?(.*)$/gm, '<h5>$1</h5>')
               .replace(/^####\s?(.*)$/gm, '<h4>$1</h4>')
               .replace(/^###\s?(.*)$/gm, '<h3>$1</h3>')
               .replace(/^##\s?(.*)$/gm, '<h2>$1</h2>')
               .replace(/^#\s?(.*)$/gm, '<h1>$1</h1>');
    html = html.replace(/^>\s?(.*)$/gm, '<blockquote>$1</blockquote>');
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/`(.*?)`/g, '<code>$1</code>');
    html = html.replace(/\n/g, '<br>');
    return html;
  };

  // Extract image URLs from AI response (markdown ![alt](url), plain URLs, base64)
  const extractImagesFromContent = (rawContent) => {
    if (!rawContent || typeof rawContent !== 'string') return { text: rawContent || '', images: [] };
    const images = [];
    const seen = new Set();
    let text = rawContent;
    // Markdown images ![alt](url)
    text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
      const u = url.trim();
      const key = u.length > 100 ? u.slice(0, 80) + u.slice(-20) : u;
      if (!seen.has(key)) {
        seen.add(key);
        images.push({ url: u, alt: (alt || 'Image').trim() || 'Image' });
        return `__IMG_${images.length - 1}__`;
      }
      return '';
    });
    // Plain image URLs
    text = text.replace(/(https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?[^\s]*)?)/gi, (match, url) => {
      const u = url.split(/[\s)]/)[0];
      const key = u.split('?')[0].split('#')[0];
      if (!seen.has(key)) {
        seen.add(key);
        images.push({ url: u, alt: 'Image' });
        return `__IMG_${images.length - 1}__`;
      }
      return '';
    });
    // Base64 data:image/...;base64,...
    text = text.replace(/(data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]+)/g, (match, dataUrl) => {
      const key = dataUrl.slice(0, 100) + dataUrl.slice(-50);
      if (!seen.has(key)) {
        seen.add(key);
        images.push({ url: dataUrl.trim(), alt: 'Generated Image' });
        return `__IMG_${images.length - 1}__`;
      }
      return '';
    });
    // Restore placeholders as empty or keep text clean (remove placeholders for final text)
    text = text.replace(/__IMG_\d+__/g, '').replace(/\n{3,}/g, '\n\n').trim();
    return { text, images };
  };

  const isImageGenerationModel = (modelId) => {
    if (!modelId) return false;
    const id = String(modelId).toLowerCase();
    return id.includes('nanobanana') || id.includes('gemini') || id.includes('image') || id.includes('dall-e') || id.includes('midjourney');
  };

  const taskRequiresImage = (task) => {
    const ct = (task.contentType || '').toLowerCase();
    const desc = (task.description || '').toLowerCase();
    return ct.includes('image') || ct.includes('photo') || desc.includes('image') || desc.includes('visual') || desc.includes('picture');
  };

  const toggleTaskExpanded = (taskId) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const handleGeneratedImageError = (taskId, imgIdx) => {
    setFailedGeneratedImages(prev => ({ ...prev, [`${taskId}_${imgIdx}`]: true }));
  };

  const downloadGeneratedImage = async (imageUrl, alt = 'Generated Image') => {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const extension = imageUrl.startsWith('data:image/')
        ? (imageUrl.match(/data:image\/([^;]+)/) || [])[1] || 'png'
        : 'png';
      const fileName = `workplan-generated-${timestamp}.${extension}`;
      if (imageUrl.startsWith('data:image/')) {
        const matches = imageUrl.match(/^data:image\/([^;]+);base64,(.+)$/);
        if (!matches) throw new Error('Invalid base64 image');
        const mimeType = matches[1];
        const base64Data = matches[2];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: `image/${mimeType}` });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading image:', error);
      window.open(imageUrl, '_blank');
    }
  };

  // Generate content for a task
  const generateContentForTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) {
      alert('Task not found.');
      return;
    }
    
    if (!task.title || !task.description) {
      alert('Please fill in Title and Content Idea before generating content.');
      return;
    }
    
    const apiKey = localStorage.getItem('openrouter_api_key');
    if (!apiKey || apiKey.trim() === '') {
      alert('Please configure your OpenRouter API key in Settings to use AI content generation.');
      return;
    }
    
    setIsGenerating(true);
    setGeneratingTaskId(taskId);
    
    try {
      // Build prompt with Content Idea and Instruction
      let prompt = `Generate ${task.contentType || 'social media'} content based on the following:

Title: ${task.title}
Content Type: ${task.contentType || 'Post'}
Content Idea: ${task.description}`;

      // Add instructions if provided
      if (task.instructions && task.instructions.trim()) {
        prompt += `\n\nAdditional Instructions: ${task.instructions}`;
      }

      prompt += `\n\nCreate engaging, well-structured content that can be used for ${task.contentType || 'social media posting'}. Make it ready to use and professional.`;

      if (taskRequiresImage(task) && isImageGenerationModel(selectedModel)) {
        prompt += `\n\nImportant: This task requires an image. Generate an actual image plus accompanying text. Prefer outputting the image as inline base64 (data:image/png;base64,...) so it displays reliably. Do not use external image URLs (e.g. imgur.com) unless they are real, permanent links—expired or placeholder URLs will fail to load. Do not only describe the image; produce a real image asset if your model supports it.`;
      }
      
      const rawContent = await generateChatMessage(
        [{ role: 'user', content: prompt }],
        selectedModel,
        apiKey
      );
      
      const rawLength = typeof rawContent === 'string' ? rawContent.length : (rawContent ? String(rawContent).length : 0);
      console.log('[Work Plan] Generation finished: raw response length =', rawLength, typeof rawContent === 'string' ? '(string)' : '(other)');
      
      const { text, images } = extractImagesFromContent(rawContent);
      console.log('[Work Plan] Extracted: text length =', (text || '').length, ', images =', images?.length ?? 0);
      
      const hasContent = (text && text.trim()) || (images && images.length > 0);
      if (!hasContent) {
        alert('The model returned no content. This can happen with image-generation models (e.g. Nano Banana) when the API response format is not supported. Try a text-only model for this task, or use Fiddy AI Chat for image generation.');
        return;
      }
      
      const updates = { generatedContent: text, generatedImages: images?.length ? images : undefined, generatedModel: selectedModel, generatedAt: new Date().toISOString() };
      setTasks(prev => {
        const updatedTasksList = prev.map(t =>
          t.id === taskId ? { ...t, ...updates } : t
        );
        if (selectedPlan) {
          localStorage.setItem(`workPlanTasks_${selectedPlan.id}`, JSON.stringify(updatedTasksList));
        }
        return updatedTasksList;
      });
      // Clear failed-image state for this task so new images render fresh
      setFailedGeneratedImages(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(key => { if (key.startsWith(`${taskId}_`)) delete next[key]; });
        return next;
      });
      
      // Build full content (text + markdown images) for Script Editor
      let fullContent = text;
      if (images && images.length) {
        fullContent += '\n\n' + images.map(img => `![${img.alt}](${img.url})`).join('\n\n');
      }
      const htmlContent = markdownToHtml(fullContent);
      
      // Try to dispatch event (works if ScriptEditor is mounted)
      window.dispatchEvent(new CustomEvent('insertContentToEditor', {
        detail: { content: htmlContent }
      }));
      
      // Also save directly to localStorage so it loads when ScriptEditor opens
      const savedContent = localStorage.getItem('scriptEditorContent') || '';
      const separator = savedContent ? '<br><br>---<br><br>' : '';
      const newContent = savedContent + separator + htmlContent;
      localStorage.setItem('scriptEditorContent', newContent);
      
      console.log('Content saved to localStorage and sent via event');
      
      alert('Content generated successfully and added to Content Editor!');
    } catch (error) {
      console.error('Content generation error:', error);
      alert('Error generating content: ' + error.message);
    } finally {
      setIsGenerating(false);
      setGeneratingTaskId(null);
    }
  };

  // Add generated content to Content Editor (text + optional images as markdown)
  const addToScriptEditor = (content, generatedImages = []) => {
    let fullContent = content || '';
    if (generatedImages && generatedImages.length) {
      fullContent += '\n\n' + generatedImages.map(img => `![${img.alt}](${img.url})`).join('\n\n');
    }
    const htmlContent = markdownToHtml(fullContent);
    
    // Try to dispatch event (works if ScriptEditor is mounted)
    window.dispatchEvent(new CustomEvent('insertContentToEditor', {
      detail: { content: htmlContent }
    }));
    
    // Also save directly to localStorage so it loads when ScriptEditor opens
    const savedContent = localStorage.getItem('scriptEditorContent') || '';
    const separator = savedContent ? '<br><br>---<br><br>' : '';
    const newContent = savedContent + separator + htmlContent;
    localStorage.setItem('scriptEditorContent', newContent);
    
    console.log('Content saved to localStorage and sent via event');
    
    alert('Content added to Content Editor!');
  };

  // Load plans and user templates from localStorage on component mount
  useEffect(() => {
    console.log('Loading plans from localStorage...');
    
    // Clear any existing sample data to ensure clean state
    const savedPlans = localStorage.getItem('workPlans');
    if (savedPlans) {
      try {
        const parsedPlans = JSON.parse(savedPlans);
        // Check if this is sample data and clear it
        if (parsedPlans.length > 0 && parsedPlans[0].id === 'sample_1') {
          console.log('Clearing existing sample data...');
          localStorage.removeItem('workPlans');
          // Clear all task data for sample plans
          parsedPlans.forEach(plan => {
            localStorage.removeItem(`workPlanTasks_${plan.id}`);
          });
          setPlans([]);
          setTasks([]);
          return;
        }
        console.log('Parsed plans:', parsedPlans);
        setPlans(parsedPlans);
      } catch (error) {
        console.error('Error parsing saved plans:', error);
        localStorage.removeItem('workPlans');
        setPlans([]);
      }
    } else {
      console.log('No saved plans found, starting with empty state');
      setPlans([]);
    }

    const savedTemplates = localStorage.getItem('userWorkPlanTemplates');
    if (savedTemplates) {
      setUserTemplates(JSON.parse(savedTemplates));
    }

    // Check if we should show overdue tasks filter (from modal navigation)
    const filterFromStorage = sessionStorage.getItem('workPlanTaskFilter');
    if (filterFromStorage === 'overdue') {
      setTaskViewFilter('overdue');
      setCurrentView('filtered-tasks');
      sessionStorage.removeItem('workPlanTaskFilter'); // Clear after using
    }
  }, []);

  // REMOVED: This was causing a circular dependency and clearing data
  // We now save manually only when needed (create, update, delete)

  // REMOVED: This was causing a circular dependency and clearing data
  // We now save manually only when needed

  // Ensure BrowserView is hidden when WorkPlan component mounts
  useEffect(() => {
    if (window.electronAPI && window.electronAPI.hideBrowserView) {
      window.electronAPI.hideBrowserView();
    }
  }, []);

  // Load tasks for selected plan
  useEffect(() => {
    if (selectedPlan) {
      const savedTasks = localStorage.getItem(`workPlanTasks_${selectedPlan.id}`);
      if (savedTasks) {
        setTasks(JSON.parse(savedTasks));
      } else {
        setTasks([]);
      }
    }
  }, [selectedPlan]);

  // Load tasks for the first plan if no plan is selected
  useEffect(() => {
    if (plans.length > 0 && !selectedPlan) {
      const firstPlan = plans[0];
      const savedTasks = localStorage.getItem(`workPlanTasks_${firstPlan.id}`);
      if (savedTasks) {
        setTasks(JSON.parse(savedTasks));
      }
    }
  }, [plans, selectedPlan]);

  // Clear tasks when all plans are deleted
  useEffect(() => {
    if (plans.length === 0) {
      setTasks([]);
      setSelectedPlan(null);
    }
  }, [plans]);

  // Save tasks to localStorage whenever tasks change
  useEffect(() => {
    if (selectedPlan) {
      localStorage.setItem(`workPlanTasks_${selectedPlan.id}`, JSON.stringify(tasks));
    }
  }, [tasks, selectedPlan]);

  // Parse task input text into structured tasks
  const parseTaskInput = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const parsed = lines.map((line, index) => {
      // Try to extract date from the line (look for common date patterns)
      const dateMatch = line.match(/(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})|(\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2})/);
      let dueDate = '';
      let taskTitle = line;

      if (dateMatch) {
        dueDate = dateMatch[0];
        taskTitle = line.replace(dateMatch[0], '').trim();
      }

      return {
        id: `temp_${index}`,
        title: taskTitle || `Task ${index + 1}`,
        description: '',
        dueDate: dueDate || '',
        priority: 'medium',
        status: 'pending',
        type: 'content-creation',
        createdAt: new Date().toISOString()
      };
    });

    setParsedTasks(parsed);
    return parsed;
  };

  // Parse content plan text (30-day plan format)
  const parseContentPlan = (planText, platform = 'facebook', startDate = new Date(), campaignName = '') => {
    if (!planText || !planText.trim()) {
      throw new Error('Content plan text is empty');
    }

    // Detect platform from text if not provided
    const textLower = planText.toLowerCase();
    let detectedPlatform = platform;
    if (!platform || platform === 'facebook') {
      if (textLower.includes('instagram')) detectedPlatform = 'instagram';
      else if (textLower.includes('youtube')) detectedPlatform = 'youtube';
      else if (textLower.includes('linkedin')) detectedPlatform = 'linkedin';
      else if (textLower.includes('twitter') || textLower.includes('x (twitter)')) detectedPlatform = 'x';
      else if (textLower.includes('tiktok')) detectedPlatform = 'tiktok';
      else detectedPlatform = 'facebook';
    }

    // Parse start date
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    // Extract campaign name from text if not provided
    let extractedCampaignName = campaignName;
    if (!extractedCampaignName) {
      const nameMatch = planText.match(/(\d+)[\s\-]day[\s\-](.+?)(?:content|plan|strategy)/i);
      if (nameMatch) {
        extractedCampaignName = `${nameMatch[1]}-Day ${nameMatch[2].trim()} Content Plan`;
      } else {
        extractedCampaignName = `${detectedPlatform.charAt(0).toUpperCase() + detectedPlatform.slice(1)} Content Plan`;
      }
    }

    // Split by day markers: ## DAY X, DAY X, **Day X:**, **Day X: (Monday) - Title**, or Day X: at line start
    const dayPattern = /(##?\s*DAY\s+(\d+)|\*\*Day\s+(\d+)\s*:[^\n]*|^DAY\s+(\d+)(?:\s|$)|^\s*Day\s+(\d+)\s*:[^\n]*)/gim;
    const dayMarkers = [];
    let match;

    while ((match = dayPattern.exec(planText)) !== null) {
      const dayNumber = parseInt(match[2] || match[3] || match[4] || match[5]);
      dayMarkers.push({
        day: dayNumber,
        index: match.index,
        fullMatch: match[0]
      });
    }

    const days = [];
    // Extract content for each day
    for (let i = 0; i < dayMarkers.length; i++) {
      const currentMarker = dayMarkers[i];
      const nextMarker = dayMarkers[i + 1];
      
      const startIndex = currentMarker.index + currentMarker.fullMatch.length;
      const endIndex = nextMarker ? nextMarker.index : planText.length;
      
      let content = planText.substring(startIndex, endIndex).trim();
      
      // Clean up markdown formatting
      content = content
        .replace(/^---+\s*/gm, '') // Remove horizontal rules
        .replace(/^##+\s*/gm, '') // Remove markdown headers
        .replace(/^\*\s*/gm, '') // Remove bullet points at start
        .replace(/^-\s*/gm, '') // Remove dashes at start
        .trim();

      // Extract title from first line (first sentence or first 60 chars)
      let title = '';
      const firstLine = content.split('\n')[0].trim();
      if (firstLine.length > 0) {
        // Try to get first sentence
        const sentenceMatch = firstLine.match(/^[^.!?]+[.!?]?/);
        if (sentenceMatch && sentenceMatch[0].length <= 100) {
          title = sentenceMatch[0].trim();
        } else {
          title = firstLine.substring(0, 60).trim();
          if (firstLine.length > 60) title += '...';
        }
      }
      
      if (!title) {
        title = `Day ${currentMarker.day}: Content`;
      }

      // Calculate due date
      const dueDate = new Date(start);
      dueDate.setDate(start.getDate() + (currentMarker.day - 1));
      const dueDateStr = dueDate.toISOString().split('T')[0];

      days.push({
        day: currentMarker.day,
        title: title,
        description: content, // Full content goes to Content Idea column
        dueDate: dueDateStr,
        priority: 'medium',
        status: 'pending',
        type: 'content-creation',
        contentType: 'post',
        platform: detectedPlatform,
        instructions: '',
        hashtags: '',
        estimatedTime: '',
        createdAt: new Date().toISOString()
      });
    }

    // Sort by day number
    days.sort((a, b) => a.day - b.day);

    if (days.length === 0) {
      throw new Error('No day markers found. Use "## DAY 1", "DAY 1", or "**Day 1: (Monday) - Title**" style markers.');
    }

    return {
      campaignName: extractedCampaignName,
      platform: detectedPlatform,
      tasks: days,
      duration: days.length
    };
  };




  // Create empty content plan structure for user input
  const createEmptyContentPlan = (platform, duration) => {
    const days = [];
    
    for (let i = 1; i <= duration; i++) {
      days.push({
        day: i,
        theme: '',
        contentType: '',
        title: '',
        description: '',
        instructions: '',
        hashtags: '',
        estimatedTime: '',
        dueDate: new Date(Date.now() + (i - 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
    }

    return days;
  };

  // Add new task to content plan
  const addNewTask = () => {
    const newTask = {
      id: Date.now().toString(),
      title: '',
      description: '',
      dueDate: '',
      priority: 'medium',
      status: 'pending',
      type: 'content-creation',
      theme: '',
      contentType: '',
      platform: selectedPlan?.platform || 'instagram',
      instructions: '',
      hashtags: '',
      estimatedTime: '',
      createdAt: new Date().toISOString()
    };
    
    setTasks([...tasks, newTask]);
  };

  // Handle CSV upload for bulk task import
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target.result;
      const lines = csv.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      
      const newTasks = [];
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          const task = {
            id: Date.now().toString() + '_' + i,
            title: values[headers.indexOf('Title')] || `Task ${i}`,
            description: values[headers.indexOf('Content Idea')] || values[headers.indexOf('Description')] || '',
            dueDate: values[headers.indexOf('Date')] || values[headers.indexOf('Due Date')] || '',
            priority: 'medium',
            status: 'pending',
            type: 'content-creation',
            theme: '',
            contentType: values[headers.indexOf('Content Type')] || '',
            platform: selectedPlan?.platform || 'instagram',
            instructions: values[headers.indexOf('Instruction')] || values[headers.indexOf('Instructions')] || '',
            hashtags: '',
            estimatedTime: '',
            createdAt: new Date().toISOString()
          };
          newTasks.push(task);
        }
      }
      
      setTasks([...tasks, ...newTasks]);
      setShowUploadModal(false);
    };
    reader.readAsText(file);
  };

  // Download sample template for content planning
  const downloadSampleTemplate = () => {
    const sampleData = [
      {
        title: '5 SEO Mistakes Content Creators Make (and How to Fix Them)',
        contentType: 'Carousel',
        contentIdea: 'Create 5-7 slides showing common SEO mistakes and quick fixes. Use engaging visuals and clear text overlays.',
        date: new Date().toISOString().split('T')[0]
      },
      {
        title: 'Before & After Website Glow-Up',
        contentType: 'Reel',
        contentIdea: 'Create a split-screen comparison showing old vs. redesigned creator website. Use trending audio and smooth transitions.',
        date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      },
      {
        title: '3 Proven Ways Content Creators Earn Passive Income',
        contentType: 'Image + Caption',
        contentIdea: 'Create a clean image with 3 key points about passive income. Write engaging caption with call-to-action.',
        date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }
    ];

    const csvContent = [
      'Title,Content Type,Content Idea,Date',
      ...sampleData.map(row => 
        `"${row.title}","${row.contentType}","${row.contentIdea}","${row.date}"`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'content-planning-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Create content plan
  const createContentPlan = () => {
    if (!contentPlanData.platform || !contentPlanData.duration) return;

    const newPlan = {
      id: Date.now().toString(),
      name: contentPlanData.campaignName || `${contentPlanData.duration}-Day ${contentPlanData.platform.charAt(0).toUpperCase() + contentPlanData.platform.slice(1)} Content Plan`,
      description: contentPlanData.description || `Custom content plan on ${contentPlanData.platform}`,
      category: 'content-planning',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + (contentPlanData.duration - 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      priority: 'high',
      status: 'active',
      createdAt: new Date().toISOString(),
      progress: 0,
      totalTasks: 0,
      completedTasks: 0,
      planType: 'content-plan',
      platform: contentPlanData.platform,
      duration: contentPlanData.duration
    };

    // Use functional state update to ensure we have the latest state
    setPlans(prevPlans => {
      const updatedPlans = [...prevPlans, newPlan];
      // Save to localStorage with the updated plans
      localStorage.setItem('workPlans', JSON.stringify(updatedPlans));
      return updatedPlans;
    });
    localStorage.setItem(`workPlanTasks_${newPlan.id}`, JSON.stringify([]));
    
    // Select the new plan to show the task editor
    setSelectedPlan(newPlan);
    setTasks([]);
    setCurrentView('plan-detail');
    
    setContentPlanData({
      days: [],
      platform: 'instagram',
      campaignName: '',
      description: '',
      duration: 30
    });
    setShowContentPlanModal(false);
  };

  // Import content plan from text
  const handleImportContentPlan = () => {
    if (!contentPlanImportText.trim()) {
      alert('Please paste a content plan to import.');
      return;
    }

    try {
      const parsed = parseContentPlan(
        contentPlanImportText,
        contentPlanImportPlatform,
        contentPlanImportStartDate,
        contentPlanImportCampaignName
      );

      // Create the campaign/plan
      const startDate = new Date(contentPlanImportStartDate);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + (parsed.duration - 1));

      const newPlan = {
        id: Date.now().toString(),
        name: parsed.campaignName,
        description: `Imported ${parsed.duration}-day content plan for ${parsed.platform}`,
        category: 'content-planning',
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        priority: 'high',
        status: 'active',
        createdAt: new Date().toISOString(),
        progress: 0,
        totalTasks: parsed.tasks.length,
        completedTasks: 0,
        planType: 'content-plan',
        platform: parsed.platform,
        duration: parsed.duration
      };

      // Create tasks with proper IDs
      const tasksWithIds = parsed.tasks.map((task, index) => ({
        ...task,
        id: `${Date.now()}_${index}`,
        planId: newPlan.id
      }));

      // Save plan and tasks
      setPlans(prevPlans => {
        const updatedPlans = [...prevPlans, newPlan];
        localStorage.setItem('workPlans', JSON.stringify(updatedPlans));
        return updatedPlans;
      });
      
      localStorage.setItem(`workPlanTasks_${newPlan.id}`, JSON.stringify(tasksWithIds));

      // Select the new plan
      setSelectedPlan(newPlan);
      setTasks(tasksWithIds);
      setCurrentView('plan-detail');

      // Reset import form
      setContentPlanImportText('');
      setContentPlanImportCampaignName('');
      setContentPlanImportPlatform('facebook');
      setContentPlanImportStartDate(new Date().toISOString().split('T')[0]);
      setShowContentPlanImportModal(false);

      alert(`Successfully imported ${parsed.tasks.length} tasks from content plan!`);
    } catch (error) {
      console.error('Error parsing content plan:', error);
      alert(`Error importing content plan: ${error.message}`);
    }
  };

  // Save current plan as template
  const savePlanAsTemplate = () => {
    if (!selectedPlan || !templateForm.name.trim()) return;

    const newTemplate = {
      id: Date.now().toString(),
      ...templateForm,
      createdAt: new Date().toISOString(),
      tasks: tasks.map(task => ({
        title: task.title,
        description: task.description,
        type: task.type,
        priority: task.priority,
        estimatedDays: 1 // Default estimate
      }))
    };

    setUserTemplates(prevTemplates => {
      const updatedTemplates = [...prevTemplates, newTemplate];
      // Save to localStorage
      localStorage.setItem('userWorkPlanTemplates', JSON.stringify(updatedTemplates));
      return updatedTemplates;
    });
    setShowSaveTemplateModal(false);
    setTemplateForm({
      name: '',
      description: '',
      category: 'social-media'
    });
  };

  // Save current plan to work plans list
  const saveCurrentPlan = () => {
    if (!selectedPlan) return;

    console.log('Saving plan:', selectedPlan.name);
    console.log('Current tasks:', tasks);

    // Update the selected plan with current data
    const updatedPlan = {
      ...selectedPlan,
      totalTasks: tasks.length,
      completedTasks: tasks.filter(task => task.status === 'completed').length,
      progress: tasks.length > 0 ? Math.round((tasks.filter(task => task.status === 'completed').length / tasks.length) * 100) : 0,
      lastModified: new Date().toISOString()
    };

    console.log('Updated plan:', updatedPlan);

    // Update plans array
    const updatedPlans = plans.map(plan => 
      plan.id === selectedPlan.id ? updatedPlan : plan
    );
    
    console.log('All plans after update:', updatedPlans);
    setPlans(updatedPlans);

    // Save to localStorage
    localStorage.setItem('workPlans', JSON.stringify(updatedPlans));
    localStorage.setItem(`workPlanTasks_${selectedPlan.id}`, JSON.stringify(tasks));
    
    console.log('Saved to localStorage - workPlans:', localStorage.getItem('workPlans'));
    console.log('Saved to localStorage - tasks:', localStorage.getItem(`workPlanTasks_${selectedPlan.id}`));

    // Update selectedPlan state
    setSelectedPlan(updatedPlan);
    
    // Clear unsaved changes flag
    setHasUnsavedChanges(false);

    // Show success message with visual feedback
    const saveButton = document.querySelector('.btn-success');
    if (saveButton) {
      const originalText = saveButton.textContent;
      saveButton.textContent = '✅ Saved!';
      saveButton.style.background = '#28a745';
      setTimeout(() => {
        saveButton.textContent = originalText;
        saveButton.style.background = '';
      }, 2000);
    }
  };

  // Create plan from user template
  const createPlanFromTemplate = (template) => {
    const newPlan = {
      id: Date.now().toString(),
      name: template.name,
      description: template.description,
      category: template.category,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      priority: 'medium',
      createdAt: new Date().toISOString(),
      status: 'active',
      progress: 0,
      totalTasks: template.tasks.length,
      completedTasks: 0
    };
    
    // Use functional state update to ensure we have the latest state
    setPlans(prevPlans => {
      const updatedPlans = [...prevPlans, newPlan];
      // Save to localStorage with the updated plans
      localStorage.setItem('workPlans', JSON.stringify(updatedPlans));
      return updatedPlans;
    });
    
    // Create tasks from template
    const templateTasks = template.tasks.map((task, index) => ({
      id: `${newPlan.id}_task_${index}`,
      ...task,
      status: 'pending',
      createdAt: new Date().toISOString(),
      dueDate: new Date(Date.now() + (index + 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    }));
    
    // Save tasks to localStorage
    localStorage.setItem(`workPlanTasks_${newPlan.id}`, JSON.stringify(templateTasks));
    setShowTemplateModal(false);
  };




  const addTask = () => {
    if (!newTask.title.trim()) return;
    
    const task = {
      id: Date.now().toString(),
      ...newTask,
      createdAt: new Date().toISOString()
    };
    
    const updatedTasks = [...tasks, task];
    setTasks(updatedTasks);
    
    // Update localStorage and plan statistics
    if (selectedPlan) {
      localStorage.setItem(`workPlanTasks_${selectedPlan.id}`, JSON.stringify(updatedTasks));
      
      const updatedPlan = {
        ...selectedPlan,
        totalTasks: updatedTasks.length,
        progress: updatedTasks.length > 0 ? Math.round((selectedPlan.completedTasks / updatedTasks.length) * 100) : 0
      };
      
      const allPlans = plans.map(plan => 
        plan.id === selectedPlan.id ? updatedPlan : plan
      );
      localStorage.setItem('workPlans', JSON.stringify(allPlans));
      setPlans(allPlans);
      setSelectedPlan(updatedPlan);
    }
    
    setNewTask({
      title: '',
      description: '',
      dueDate: '',
      priority: 'medium',
      status: 'pending',
      type: 'content-creation'
    });
  };

  const updateTaskStatus = (taskId, status) => {
    const updatedTasks = tasks.map(task => 
      task.id === taskId ? { ...task, status } : task
    );
    setTasks(updatedTasks);
    
    // Update localStorage for tasks
    if (selectedPlan) {
      localStorage.setItem(`workPlanTasks_${selectedPlan.id}`, JSON.stringify(updatedTasks));
      
      // Update plan statistics
      const completedCount = updatedTasks.filter(task => task.status === 'completed').length;
      const totalCount = updatedTasks.length;
      const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
      
      const updatedPlan = {
        ...selectedPlan,
        totalTasks: totalCount,
        completedTasks: completedCount,
        progress: progress
      };
      
      // Update plan in localStorage
      const allPlans = plans.map(plan => 
        plan.id === selectedPlan.id ? updatedPlan : plan
      );
      localStorage.setItem('workPlans', JSON.stringify(allPlans));
      setPlans(allPlans);
      setSelectedPlan(updatedPlan);
    }
  };

  const updateTaskField = (taskId, field, value) => {
    const updatedTasks = tasks.map(task =>
      task.id === taskId ? { ...task, [field]: value } : task
    );
    setTasks(updatedTasks);
    setHasUnsavedChanges(true);
    if (selectedPlan) {
      localStorage.setItem(`workPlanTasks_${selectedPlan.id}`, JSON.stringify(updatedTasks));
    }
  };

  const deleteTask = (taskId) => {
    const updatedTasks = tasks.filter(task => task.id !== taskId);
    setTasks(updatedTasks);
    
    // Update localStorage and plan statistics
    if (selectedPlan) {
      localStorage.setItem(`workPlanTasks_${selectedPlan.id}`, JSON.stringify(updatedTasks));
      
      const updatedPlan = {
        ...selectedPlan,
        totalTasks: updatedTasks.length,
        progress: updatedTasks.length > 0 ? Math.round((selectedPlan.completedTasks / updatedTasks.length) * 100) : 0
      };
      
      const allPlans = plans.map(plan => 
        plan.id === selectedPlan.id ? updatedPlan : plan
      );
      localStorage.setItem('workPlans', JSON.stringify(allPlans));
      setPlans(allPlans);
      setSelectedPlan(updatedPlan);
    }
  };

  const deletePlan = (planId) => {
    const updatedPlans = plans.filter(plan => plan.id !== planId);
    setPlans(updatedPlans);
    
    // Update localStorage
    localStorage.setItem('workPlans', JSON.stringify(updatedPlans));
    
    if (selectedPlan && selectedPlan.id === planId) {
      setSelectedPlan(null);
      setCurrentView('dashboard');
    }
    localStorage.removeItem(`workPlanTasks_${planId}`);
  };

  const deleteTemplate = (templateId) => {
    setUserTemplates(prevTemplates => {
      const updatedTemplates = prevTemplates.filter(template => template.id !== templateId);
      // Save to localStorage
      localStorage.setItem('userWorkPlanTemplates', JSON.stringify(updatedTemplates));
      return updatedTemplates;
    });
  };

  const getPlanProgress = (plan) => {
    const planTasks = JSON.parse(localStorage.getItem(`workPlanTasks_${plan.id}`) || '[]');
    const completed = planTasks.filter(task => task.status === 'completed').length;
    const total = planTasks.length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getOverdueTasks = () => {
    const today = new Date().toISOString().split('T')[0];
    return tasks.filter(task => 
      task.status !== 'completed' && 
      task.dueDate && 
      task.dueDate < today
    );
  };

  const getUpcomingTasks = () => {
    const today = new Date().toISOString().split('T')[0];
    const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return tasks.filter(task => 
      task.status !== 'completed' && 
      task.dueDate && 
      task.dueDate >= today && 
      task.dueDate <= nextWeek
    );
  };

  // Get all upcoming tasks from all plans for dashboard display
  const getAllUpcomingTasks = () => {
    const today = new Date().toISOString().split('T')[0];
    const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    let allUpcomingTasks = [];
    
    plans.forEach(plan => {
      const planTasks = JSON.parse(localStorage.getItem(`workPlanTasks_${plan.id}`) || '[]');
      const upcomingTasks = planTasks.filter(task => 
        task.status !== 'completed' && 
        task.dueDate && 
        task.dueDate >= today && 
        task.dueDate <= nextWeek
      ).map(task => ({
        ...task,
        planId: plan.id, // Ensure planId is included
        planName: plan.name,
        planCategory: plan.category
      }));
      
      allUpcomingTasks = [...allUpcomingTasks, ...upcomingTasks];
    });
    
    // Sort by due date (current day first, then upcoming days)
    return allUpcomingTasks.sort((a, b) => {
      if (a.dueDate === today) return -1;
      if (b.dueDate === today) return 1;
      return a.dueDate.localeCompare(b.dueDate);
    }).slice(0, 10); // Limit to top 10
  };

  // Get all overdue tasks from all plans for dashboard display
  const getAllOverdueTasks = () => {
    const today = new Date().toISOString().split('T')[0];
    
    let allOverdueTasks = [];
    
    plans.forEach(plan => {
      const planTasks = JSON.parse(localStorage.getItem(`workPlanTasks_${plan.id}`) || '[]');
      const overdueTasks = planTasks.filter(task => 
        task.status !== 'completed' && 
        task.dueDate && 
        task.dueDate < today
      ).map(task => ({
        ...task,
        planId: plan.id, // Ensure planId is included
        planName: plan.name,
        planCategory: plan.category
      }));
      
      allOverdueTasks = [...allOverdueTasks, ...overdueTasks];
    });
    
    return allOverdueTasks.slice(0, 10); // Limit to top 10
  };

  // Get all pending tasks from all plans
  const getAllPendingTasks = () => {
    let allPendingTasks = [];
    
    plans.forEach(plan => {
      const planTasks = JSON.parse(localStorage.getItem(`workPlanTasks_${plan.id}`) || '[]');
      const pendingTasks = planTasks.filter(task => 
        task.status === 'pending'
      ).map(task => ({
        ...task,
        planId: plan.id, // Ensure planId is included
        planName: plan.name,
        planCategory: plan.category
      }));
      
      allPendingTasks = [...allPendingTasks, ...pendingTasks];
    });
    
    return allPendingTasks;
  };

  // Get all completed tasks from all plans
  const getAllCompletedTasks = () => {
    let allCompletedTasks = [];
    
    plans.forEach(plan => {
      const planTasks = JSON.parse(localStorage.getItem(`workPlanTasks_${plan.id}`) || '[]');
      const completedTasks = planTasks.filter(task => 
        task.status === 'completed'
      ).map(task => ({
        ...task,
        planId: plan.id, // Ensure planId is included
        planName: plan.name,
        planCategory: plan.category
      }));
      
      allCompletedTasks = [...allCompletedTasks, ...completedTasks];
    });
    
    return allCompletedTasks;
  };

  // Format date from YYYY-MM-DD to YYYY-MM-DD (keep consistent format)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    // Return the date in YYYY-MM-DD format to maintain consistency
    return dateString;
  };

  // Get filtered tasks based on current filter
  const getFilteredTasks = () => {
    switch (taskFilter) {
      case 'pending':
        return tasks.filter(task => task.status === 'pending');
      case 'in-progress':
        return tasks.filter(task => task.status === 'in-progress');
      case 'completed':
        return tasks.filter(task => task.status === 'completed');
      default:
        return tasks;
    }
  };

  // Handle item click (task or plan) - navigate to plan detail view
  const handleItemClick = (item) => {
    console.log('🔍 handleItemClick called with:', item);
    console.log('🔍 taskViewFilter:', taskViewFilter);
    console.log('🔍 Available plans:', plans);
    
    // Check if item is a plan (has properties like name, category, etc.)
    if (item && (item.name || item.category)) {
      // It's a plan, navigate to plan detail
      console.log('✅ Item is a plan, navigating to plan detail');
      setSelectedPlan(item);
      setCurrentView('plan-detail');
    } else if (item && item.planId) {
      // It's a task, find the plan and navigate to plan detail
      console.log('✅ Item is a task, looking for plan with ID:', item.planId);
      const plan = plans.find(p => p.id === item.planId);
      if (plan) {
        console.log('✅ Found plan:', plan);
        setSelectedPlan(plan);
        setCurrentView('plan-detail');
      } else {
        console.error('❌ Plan not found for task:', item);
        alert('Plan not found for this task');
      }
    } else {
      console.error('❌ Invalid item clicked:', item);
    }
  };

  const renderDashboard = () => (
    <div className="workplan-dashboard">
      <div className="dashboard-header">
        <h1>🚀 Work Plan Dashboard</h1>
        <div className="dashboard-actions">
          <button className="btn btn-primary" onClick={() => setShowContentPlanModal(true)}>
            ➕ Create Plan
          </button>
          <button className="btn btn-primary" onClick={() => setShowContentPlanImportModal(true)}>
            📋 Import Content Plan (Paste Text)
          </button>
          <button className="btn btn-secondary" onClick={() => setShowTemplateModal(true)}>
            📋 My Templates
          </button>
          <button className="btn btn-info" onClick={downloadSampleTemplate}>
            📥 Sample Template
          </button>
          <button className="btn btn-success" onClick={() => {
            setSelectedPlansForExport(plans.map(p => p.id));
            setShowExportModal(true);
          }}>
            📤 Export Work Plans
          </button>
          <button className="btn btn-warning" onClick={() => setShowImportModal(true)}>
            📥 Import Work Plans
          </button>

          <button className="btn btn-danger" onClick={() => {
            if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
              localStorage.removeItem('workPlans');
              localStorage.removeItem('userWorkPlanTemplates');
              // Clear all task data
              plans.forEach(plan => {
                localStorage.removeItem(`workPlanTasks_${plan.id}`);
              });
              setPlans([]);
              setTasks([]);
              setUserTemplates([]);
              setSelectedPlan(null);
            }
          }}>
            🗑️ Clear All Data
          </button>
        </div>
      </div>

      <div className="dashboard-stats">
        <div 
          className="stat-card clickable-stat" 
          onClick={() => {
            setTaskViewFilter('all-plans');
            setCurrentView('filtered-tasks');
          }}
          style={{ cursor: 'pointer' }}
          title="Click to view all plans"
        >
          <div className="stat-icon">📊</div>
          <div className="stat-number">{plans.length}</div>
          <div className="stat-label">Total Plans</div>
        </div>
        <div 
          className="stat-card clickable-stat" 
          onClick={() => {
            setTaskViewFilter('active-plans');
            setCurrentView('filtered-tasks');
          }}
          style={{ cursor: 'pointer' }}
          title="Click to view active plans"
        >
          <div className="stat-icon">🚀</div>
          <div className="stat-number">{plans.filter(p => p.status === 'active').length}</div>
          <div className="stat-label">Active Plans</div>
        </div>
        <div 
          className="stat-card clickable-stat" 
          onClick={() => {
            setTaskViewFilter('overdue');
            setCurrentView('filtered-tasks');
          }}
          style={{ cursor: 'pointer' }}
          title="Click to view overdue tasks"
        >
          <div className="stat-icon">⚠️</div>
          <div className="stat-number">{getAllOverdueTasks().length}</div>
          <div className="stat-label">Overdue Tasks</div>
        </div>
        <div 
          className="stat-card clickable-stat" 
          onClick={() => {
            setTaskViewFilter('pending');
            setCurrentView('filtered-tasks');
          }}
          style={{ cursor: 'pointer' }}
          title="Click to view pending tasks"
        >
          <div className="stat-icon">⏳</div>
          <div className="stat-number">{getAllPendingTasks().length}</div>
          <div className="stat-label">Pending Tasks</div>
        </div>
        <div 
          className="stat-card clickable-stat" 
          onClick={() => {
            setTaskViewFilter('completed');
            setCurrentView('filtered-tasks');
          }}
          style={{ cursor: 'pointer' }}
          title="Click to view completed tasks"
        >
          <div className="stat-icon">✅</div>
          <div className="stat-number">{getAllCompletedTasks().length}</div>
          <div className="stat-label">Completed Tasks</div>
        </div>
      </div>

      <div className="dashboard-info">
        <div className="info-banner">
          <h4>💡 How It Works</h4>
          <div className="info-steps">
            <div className="info-step">
              <span className="step-number">1</span>
              <span>Create a plan or content campaign</span>
            </div>
            <div className="info-step">
              <span className="step-number">2</span>
              <span>Add tasks and customize your content</span>
            </div>
            <div className="info-step">
              <span className="step-number">3</span>
              <span><strong>Click "Save Plan" to keep your changes</strong></span>
            </div>
            <div className="info-step">
              <span className="step-number">4</span>
              <span>Use "Save as Template" to reuse the structure</span>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="plans-section">
          <h2>📅 Active Plans</h2>
          <div className="plans-grid">
            {plans.filter(plan => plan.status === 'active').length > 0 ? (
              plans.filter(plan => plan.status === 'active').map(plan => (
                <div key={plan.id} className="plan-card" onClick={() => {
                  setSelectedPlan(plan);
                  setCurrentView('plan-detail');
                }}>
                  <div className="plan-header">
                    <h3>{plan.name}</h3>
                    <span className={`priority-badge ${plan.priority}`}>{plan.priority}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                  <div className="plan-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${getPlanProgress(plan)}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">{getPlanProgress(plan)}%</span>
                  </div>
                  <div className="plan-meta">
                    <span className="plan-category">{plan.category}</span>
                    <span className="plan-dates">{formatDate(plan.startDate)} - {formatDate(plan.endDate)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-plans-message">
                <div className="no-plans-icon">📋</div>
                <h3>No Active Plans</h3>
                <p>Create your first work plan to get started!</p>
                <button className="btn btn-primary" onClick={() => setShowContentPlanModal(true)}>
                  ➕ Create Plan
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="tasks-overview-section">
          <div className="tasks-overview-grid">
            {/* Overdue Tasks Column */}
            <div className="tasks-column overdue-column">
              <h2>⚠️ Overdue Tasks</h2>
              <div className="tasks-list">
                {getAllOverdueTasks().length > 0 ? (
                  getAllOverdueTasks().map(task => (
                    <div 
                      key={task.id} 
                      className="task-quick-view overdue clickable-card"
                      onClick={() => handleItemClick(task)}
                    >
                      <div className="task-header">
                        <span className="task-plan">{task.planName}</span>
                        <span className="task-date overdue-date">Due: {formatDate(task.dueDate)}</span>
                      </div>
                      <h4 className="task-title">{task.title}</h4>
                      <div className="task-meta">
                        <span className="task-type">{task.contentType || 'Task'}</span>
                        <span className="task-priority priority-badge">{task.priority}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-tasks-message">
                    <div className="no-tasks-icon">🎉</div>
                    <h3>No Overdue Tasks</h3>
                    <p>Great job! You're all caught up.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upcoming Tasks Column */}
            <div className="tasks-column upcoming-column">
              <h2>📅 Upcoming Tasks</h2>
              <div className="tasks-list">
                {getAllUpcomingTasks().length > 0 ? (
                  getAllUpcomingTasks().map(task => (
                    <div 
                      key={task.id} 
                      className="task-quick-view upcoming clickable-card"
                      onClick={() => handleItemClick(task)}
                    >
                      <div className="task-header">
                        <span className="task-plan">{task.planName}</span>
                        <span className="task-date upcoming-date">Due: {formatDate(task.dueDate)}</span>
                      </div>
                      <h4 className="task-title">{task.title}</h4>
                      <div className="task-meta">
                        <span className="task-type">{task.contentType || 'Task'}</span>
                        <span className="task-priority priority-badge">{task.priority}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="no-tasks-message">
                      <div className="no-tasks-icon">📋</div>
                      <h3>No Upcoming Tasks</h3>
                      <p>Create some tasks to get started!</p>
                    </div>
                    <div className="no-tasks-stats">
                      <div className="stat-item">
                        <span className="stat-number">{tasks.filter(t => t.status === 'completed').length}</span>
                        <span className="stat-label">Completed</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{tasks.filter(t => t.status === 'pending').length}</span>
                        <span className="stat-label">Pending</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFilteredTasks = () => {
    let filteredItems = [];
    let title = '';
    let emptyMessage = '';
    let emptyIcon = '';

    switch (taskViewFilter) {
      case 'pending':
        filteredItems = getAllPendingTasks();
        title = 'Pending Tasks';
        emptyMessage = 'No pending tasks. All tasks are in progress or completed!';
        emptyIcon = '⏳';
        break;
      case 'completed':
        filteredItems = getAllCompletedTasks();
        title = 'Completed Tasks';
        emptyMessage = 'No completed tasks yet. Complete some tasks to see them here.';
        emptyIcon = '✅';
        break;
      case 'overdue':
        filteredItems = getAllOverdueTasks();
        title = 'Overdue Tasks';
        emptyMessage = 'Great job! You have no overdue tasks.';
        emptyIcon = '🎉';
        break;
      case 'all-plans':
        filteredItems = plans;
        title = 'All Plans';
        emptyMessage = 'No plans yet. Create your first plan to get started!';
        emptyIcon = '📋';
        break;
      case 'active-plans':
        filteredItems = plans.filter(p => p.status === 'active');
        title = 'Active Plans';
        emptyMessage = 'No active plans. Create a plan to get started!';
        emptyIcon = '🚀';
        break;
      default:
        filteredItems = [];
        title = 'Filtered View';
        emptyMessage = 'No items to display.';
        emptyIcon = '📋';
    }

    return (
      <div className="workplan-filtered-view">
        <div className="filtered-view-header">
          <button className="btn btn-back" onClick={() => {
            setCurrentView('dashboard');
            setTaskViewFilter(null);
          }}>
            ← Back to Dashboard
          </button>
          <h1>{title}</h1>
          <div className="filtered-count">
            {filteredItems.length} {taskViewFilter === 'all-plans' || taskViewFilter === 'active-plans' ? 'plan(s)' : 'task(s)'}
          </div>
        </div>

        <div className="filtered-content">
          {filteredItems.length > 0 ? (
            <div className="filtered-items-list">
              {(taskViewFilter === 'all-plans' || taskViewFilter === 'active-plans') ? (
                // Render plans
                filteredItems.map(plan => (
                  <div 
                    key={plan.id} 
                    className="plan-card clickable-card"
                    onClick={() => handleItemClick(plan)}
                  >
                    <div className="plan-header">
                      <h3>{plan.name}</h3>
                      <span className={`priority-badge ${plan.priority}`}>{plan.priority}</span>
                    </div>
                    <p className="plan-description">{plan.description}</p>
                    <div className="plan-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${getPlanProgress(plan)}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">{getPlanProgress(plan)}%</span>
                    </div>
                    <div className="plan-meta">
                      <span className="plan-category">{plan.category}</span>
                      <span className="plan-dates">{formatDate(plan.startDate)} - {formatDate(plan.endDate)}</span>
                    </div>
                  </div>
                ))
              ) : (
                // Render tasks
                filteredItems.map(task => (
                  <div 
                    key={task.id} 
                    className="task-quick-view clickable-card"
                    onClick={() => handleItemClick(task)}
                  >
                    <div className="task-header">
                      <span className="task-plan">{task.planName || 'Unknown Plan'}</span>
                      {task.dueDate && (
                        <span className={`task-date ${taskViewFilter === 'overdue' ? 'overdue-date' : 'upcoming-date'}`}>
                          Due: {formatDate(task.dueDate)}
                        </span>
                      )}
                    </div>
                    <h4 className="task-title">{task.title}</h4>
                    {task.description && (
                      <p className="task-description">{task.description}</p>
                    )}
                    <div className="task-meta">
                      <span className="task-type">{task.contentType || 'Task'}</span>
                      <span className={`task-priority priority-badge ${task.priority}`}>{task.priority}</span>
                      <span className={`task-status status-badge ${task.status}`}>{task.status}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="no-items-message">
              <div className="no-items-icon">{emptyIcon}</div>
              <h3>{title}</h3>
              <p>{emptyMessage}</p>
              {(taskViewFilter === 'all-plans' || taskViewFilter === 'active-plans') && (
                <button className="btn btn-primary" onClick={() => {
                  setCurrentView('dashboard');
                  setShowContentPlanModal(true);
                }}>
                  ➕ Create Plan
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPlanDetail = () => (
    <div className="workplan-detail">
      <div className="detail-header">
        <button className="btn btn-back" onClick={() => setCurrentView('dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>{selectedPlan.name}</h1>
        <div className="plan-actions">
          <button 
            className={`btn ${hasUnsavedChanges ? 'btn-warning' : 'btn-success'}`} 
            onClick={saveCurrentPlan}
          >
            {hasUnsavedChanges ? '⚠️ Save Plan*' : '💾 Save Plan'}
          </button>
          <button className="btn btn-secondary" onClick={() => setShowSaveTemplateModal(true)}>
            📋 Save as Template
          </button>
          <button className="btn btn-danger" onClick={() => deletePlan(selectedPlan.id)}>
            🗑️ Delete Plan
          </button>
        </div>
        <div className="plan-actions-info">
          <small>💡 <strong>Save Plan</strong> to keep your changes • <strong>Save as Template</strong> to reuse this plan structure</small>
        </div>
      </div>

      <div className="plan-info">
        <div className="info-card">
          <h3>Plan Details</h3>
          <p><strong>Description:</strong> {selectedPlan.description}</p>
          <p><strong>Category:</strong> {selectedPlan.category}</p>
          <p><strong>Priority:</strong> <span className={`priority-badge ${selectedPlan.priority}`}>{selectedPlan.priority}</span></p>
          <p><strong>Duration:</strong> {formatDate(selectedPlan.startDate)} to {formatDate(selectedPlan.endDate)}</p>
          <p><strong>Progress:</strong> {getPlanProgress(selectedPlan)}%</p>
        </div>
      </div>

      <div className="tasks-section">
        <div className="tasks-header">
          <h2>📝 Tasks</h2>
          <div className="task-actions-header">
            {selectedPlan?.category === 'content-planning' ? (
              <>
                <button className="btn btn-primary" onClick={addNewTask}>
                  ➕ Add New Task
                </button>
                <button className="btn btn-secondary" onClick={() => setShowUploadModal(true)}>
                  📤 Upload CSV
                </button>
              </>
            ) : (
              <button className="btn btn-primary" onClick={addTask}>
                ➕ Add Task
              </button>
            )}
          </div>
        </div>

        <div className="task-filters">
          <button 
            className={`filter-btn ${taskFilter === 'all' ? 'active' : ''}`}
            onClick={() => setTaskFilter('all')}
          >
            All ({tasks.length})
          </button>
          <button 
            className={`filter-btn ${taskFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setTaskFilter('pending')}
          >
            Pending ({tasks.filter(t => t.status === 'pending').length})
          </button>
          <button 
            className={`filter-btn ${taskFilter === 'in-progress' ? 'active' : ''}`}
            onClick={() => setTaskFilter('in-progress')}
          >
            In Progress ({tasks.filter(t => t.status === 'in-progress').length})
          </button>
          <button 
            className={`filter-btn ${taskFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setTaskFilter('completed')}
          >
            Completed ({tasks.filter(t => t.status === 'completed').length})
          </button>
        </div>
        {tasks.length > 0 && (
          <div className={`task-save-reminder ${hasUnsavedChanges ? 'has-unsaved-changes' : ''}`}>
            <small>
              {hasUnsavedChanges ? (
                <>
                  ⚠️ <strong>You have unsaved changes!</strong> Click "Save Plan" to keep your progress.
                </>
              ) : (
                <>
                  💾 <strong>Remember to save your plan</strong> after making changes to keep your progress!
                </>
              )}
            </small>
          </div>
        )}

        {/* Model Selector */}
        {availableModels && availableModels.length > 0 && (
          <div className="workplan-model-selector-container">
            <SearchableSelect
              id="workplan-model-selector"
              value={selectedModel}
              onChange={setSelectedModel}
              options={availableModels.map(model => {
                const isUnavailable = model.isAvailable === false;
                return {
                  id: model.id,
                  value: model.id,
                  label: `${model.name} ${model.isFree ? '(FREE)' : '(PAID)'} ${isUnavailable ? '[Unavailable]' : ''}`,
                  disabled: isUnavailable,
                };
              })}
              placeholder="Select AI Model..."
              className="workplan-model-selector"
              label="AI Model:"
            />
          </div>
        )}

        <div className="tasks-table">
          <div className="table-header">
            <div className="table-cell checkbox-cell">Status</div>
            <div className="table-cell title-cell">Title</div>
            <div className="table-cell type-cell">Content Type</div>
            <div className="table-cell date-cell">Date</div>
            <div className="table-cell idea-cell">Content Idea</div>
            <div className="table-cell instruction-cell">Instruction</div>
            <div className="table-cell generated-content-cell">Generated Content</div>
            <div className="table-cell actions-cell">Actions</div>
          </div>
          
          {getFilteredTasks().map(task => (
            <div key={task.id} className={`table-row ${task.status}`}>
              <div className="table-cell checkbox-cell">
                <select
                  value={task.status}
                  onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                  className="status-select"
                >
                  <option value="pending">⏳ Pending</option>
                  <option value="in-progress">🔄 In Progress</option>
                  <option value="completed">✅ Completed</option>
                </select>
              </div>
              <div className="table-cell title-cell">
                <input
                  type="text"
                  value={task.title}
                  onChange={(e) => updateTaskField(task.id, 'title', e.target.value)}
                  className="table-input"
                  placeholder="Enter title"
                />
              </div>
              <div className="table-cell type-cell">
                <input
                  type="text"
                  value={task.contentType || ''}
                  onChange={(e) => updateTaskField(task.id, 'contentType', e.target.value)}
                  className="table-input"
                  placeholder="e.g., Carousel, Reel"
                />
              </div>
              <div className="table-cell date-cell">
                <input
                  type="date"
                  value={task.dueDate || ''}
                  onChange={(e) => updateTaskField(task.id, 'dueDate', e.target.value)}
                  className="table-input"
                />
              </div>
              <div className="table-cell idea-cell">
                <textarea
                  value={task.description || ''}
                  onChange={(e) => updateTaskField(task.id, 'description', e.target.value)}
                  className="table-textarea"
                  placeholder="Content details"
                  rows={2}
                />
              </div>
              <div className="table-cell instruction-cell">
                <textarea
                  value={task.instructions || ''}
                  onChange={(e) => updateTaskField(task.id, 'instructions', e.target.value)}
                  className="table-textarea"
                  placeholder="Additional AI instructions"
                  rows={2}
                />
              </div>
              <div className="table-cell generated-content-cell">
                {task.generatedContent || (task.generatedImages && task.generatedImages.length) ? (
                  <div className="generated-content-display">
                    <button
                      type="button"
                      className="generated-content-toggle"
                      onClick={() => toggleTaskExpanded(task.id)}
                      aria-expanded={!!expandedTasks[task.id]}
                    >
                      <span className="generated-content-toggle-icon">{expandedTasks[task.id] ? '▼' : '▶'}</span>
                      <span className="generated-content-toggle-label">
                        📄 Generated Content
                        {task.generatedImages && task.generatedImages.length ? ` + ${task.generatedImages.length} image(s)` : ''}
                      </span>
                    </button>
                    {expandedTasks[task.id] && (
                      <div className="generated-content-expanded">
                          {task.generatedImages && task.generatedImages.length > 0 && (
                          <div className="generated-content-images">
                            {task.generatedImages.map((img, idx) => {
                              const failedKey = `${task.id}_${idx}`;
                              const failed = failedGeneratedImages[failedKey];
                              const isUrl = img.url.startsWith('http://') || img.url.startsWith('https://');
                              return (
                                <div key={idx} className="workplan-generated-image">
                                  {failed ? (
                                    <div className="workplan-generated-image-fallback">
                                      <span className="workplan-generated-image-fallback-icon">🖼️</span>
                                      <p className="workplan-generated-image-fallback-text">Image link unavailable</p>
                                      <p className="workplan-generated-image-fallback-hint">The model returned a URL that could not be loaded (expired or invalid). Try regenerating or use a model that outputs inline image data.</p>
                                      {isUrl && (
                                        <button
                                          type="button"
                                          className="workplan-download-image-btn"
                                          onClick={() => window.open(img.url, '_blank')}
                                          title="Open link"
                                        >
                                          🔗 Open link
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <>
                                      <img
                                        src={img.url}
                                        alt={img.alt}
                                        onError={() => handleGeneratedImageError(task.id, idx)}
                                      />
                                      <button
                                        type="button"
                                        className="workplan-download-image-btn"
                                        onClick={() => downloadGeneratedImage(img.url, img.alt)}
                                        title="Download image"
                                      >
                                        ⬇️ Download
                                      </button>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {task.generatedContent && (
                          <div className="generated-content-text" dangerouslySetInnerHTML={{ __html: markdownToHtml(task.generatedContent) }} />
                        )}
                        <div className="generated-content-actions">
                          <button
                            className="btn btn-small btn-primary add-to-editor-btn"
                            onClick={() => addToScriptEditor(task.generatedContent, task.generatedImages)}
                            title="Add to Content Editor"
                          >
                            ➕ Add to Editor
                          </button>
                          <button
                            className="btn btn-small btn-secondary regenerate-btn"
                            onClick={() => generateContentForTask(task.id)}
                            disabled={isGenerating && generatingTaskId === task.id}
                            title="Regenerate content"
                          >
                            {isGenerating && generatingTaskId === task.id ? '⏳ Generating...' : '🔄 Regenerate'}
                          </button>
                        </div>
                        {task.generatedModel && (
                          <div className="generated-content-meta">
                            <small>Model: {availableModels.find(m => m.id === task.generatedModel)?.name || task.generatedModel}</small>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="generate-content-container">
                    <button
                      className="btn btn-small btn-primary generate-content-btn"
                      onClick={() => generateContentForTask(task.id)}
                      disabled={isGenerating && generatingTaskId === task.id}
                      title="Generate content using AI"
                    >
                      {isGenerating && generatingTaskId === task.id ? '⏳ Generating...' : '✨ Generate'}
                    </button>
                  </div>
                )}
              </div>
              <div className="table-cell actions-cell">
                <button 
                  className="btn btn-small btn-danger"
                  onClick={() => deleteTask(task.id)}
                  title="Delete task"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );











  const renderTaskParserModal = () => (
    <div className="modal-overlay" onClick={() => setShowTaskParser(false)}>
      <div className="modal-content task-parser-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Review & Edit Tasks</h2>
          <button className="modal-close" onClick={() => setShowTaskParser(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="parsed-tasks-list">
            {parsedTasks.map((task, index) => (
              <div key={task.id} className="parsed-task-item">
                <div className="parsed-task-header">
                  <span className="task-number">Task {index + 1}</span>
                  <input
                    type="text"
                    value={task.title}
                    onChange={(e) => {
                      const updated = [...parsedTasks];
                      updated[index].title = e.target.value;
                      setParsedTasks(updated);
                    }}
                    placeholder="Task title"
                    className="task-title-input"
                  />
                </div>
                <div className="parsed-task-details">
                  <input
                    type="text"
                    value={task.dueDate}
                    onChange={(e) => {
                      const updated = [...parsedTasks];
                      updated[index].dueDate = e.target.value;
                      setParsedTasks(updated);
                    }}
                    placeholder="Due date (YYYY-MM-DD)"
                    className="task-date-input"
                  />
                  <select
                    value={task.priority}
                    onChange={(e) => {
                      const updated = [...parsedTasks];
                      updated[index].priority = e.target.value;
                      setParsedTasks(updated);
                    }}
                    className="task-priority-select"
                  >
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>
                </div>
                <button
                  className="btn btn-danger btn-small"
                  onClick={() => {
                    setParsedTasks(parsedTasks.filter((_, i) => i !== index));
                  }}
                >
                  🗑️ Remove
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowTaskParser(false)}>
            Back to Edit
          </button>
                      <button 
              className="btn btn-primary" 
              onClick={() => setShowTaskParser(false)}
              disabled={parsedTasks.length === 0}
            >
              Close
            </button>
        </div>
      </div>
    </div>
  );

  const renderTemplateModal = () => (
    <div className="modal-overlay" onClick={() => setShowTemplateModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>My Saved Templates</h2>
          <button className="modal-close" onClick={() => setShowTemplateModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          {userTemplates.length === 0 ? (
            <div className="no-templates">
              <p>No saved templates yet. Create a campaign and save it as a template to reuse later!</p>
            </div>
          ) : (
            <div className="templates-grid">
              {userTemplates.map(template => (
                <div key={template.id} className="template-card">
                  <div className="template-header">
                    <h3>{template.name}</h3>
                    <button 
                      className="btn btn-danger btn-small"
                      onClick={() => deleteTemplate(template.id)}
                    >
                      🗑️
                    </button>
                  </div>
                  <p>{template.description}</p>
                  <div className="template-meta">
                    <span className="template-category">{template.category}</span>
                    <span className="template-tasks">{template.tasks.length} tasks</span>
                  </div>
                  <button 
                    className="btn btn-primary"
                    onClick={() => createPlanFromTemplate(template)}
                  >
                    📋 Use This Template
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSaveTemplateModal = () => (
    <div className="modal-overlay" onClick={() => setShowSaveTemplateModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Save Campaign as Template</h2>
          <button className="modal-close" onClick={() => setShowSaveTemplateModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Template Name</label>
            <input
              type="text"
              value={templateForm.name}
              onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
              placeholder="Enter template name"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={templateForm.description}
              onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
              placeholder="Enter template description"
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select
              value={templateForm.category}
              onChange={(e) => setTemplateForm({...templateForm, category: e.target.value})}
            >
              <option value="social-media">Social Media</option>
              <option value="marketing">Marketing</option>
              <option value="content">Content</option>
              <option value="product">Product</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div className="template-preview">
            <h4>Template Preview</h4>
            <p><strong>Campaign:</strong> {selectedPlan?.name}</p>
            <p><strong>Tasks:</strong> {tasks.length} tasks will be included</p>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowSaveTemplateModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={savePlanAsTemplate}
            disabled={!templateForm.name.trim()}
          >
            Save as Template
          </button>
        </div>
      </div>
    </div>
  );

  const renderContentPlanImportModal = () => {
    let parsedPreview = null;
    let previewError = null;
    
    if (contentPlanImportText.trim()) {
      try {
        const parsed = parseContentPlan(
          contentPlanImportText,
          contentPlanImportPlatform,
          contentPlanImportStartDate,
          contentPlanImportCampaignName
        );
        parsedPreview = parsed;
      } catch (error) {
        previewError = error.message;
      }
    }

    return (
      <div className="modal-overlay" onClick={() => setShowContentPlanImportModal(false)}>
        <div className="modal-content content-plan-import-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>📋 Import Content Plan (Paste Text)</h2>
            <button className="modal-close" onClick={() => setShowContentPlanImportModal(false)}>✕</button>
          </div>
          <div className="modal-body">
            <div style={{ 
              background: '#e3f2fd', 
              padding: '12px 16px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #90caf9'
            }}>
              <strong>📝 Paste Your Content Plan Below</strong>
              <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem' }}>
                This is for <strong>pasting text content</strong> directly into the textarea below. 
                If you want to import a JSON file instead, use the "Import Work Plans" button.
              </p>
            </div>
            <div className="form-group">
              <label>Campaign Name (Optional - will be auto-detected if not provided)</label>
              <input
                type="text"
                value={contentPlanImportCampaignName}
                onChange={(e) => setContentPlanImportCampaignName(e.target.value)}
                placeholder="e.g., 30-Day Facebook Content Plan"
              />
            </div>

            <div className="form-group">
              <label>Platform</label>
              <select
                value={contentPlanImportPlatform}
                onChange={(e) => setContentPlanImportPlatform(e.target.value)}
              >
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="youtube">YouTube</option>
                <option value="linkedin">LinkedIn</option>
                <option value="x">X (Twitter)</option>
                <option value="tiktok">TikTok</option>
                <option value="whatsapp-status">WhatsApp Status</option>
                <option value="blog">Blog</option>
                <option value="ecommerce">E-commerce</option>
                <option value="bluesky">Bluesky</option>
                <option value="website">Website</option>
                <option value="multi-platform">Multi-Platform</option>
                <option value="others">Others</option>
              </select>
            </div>

            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={contentPlanImportStartDate}
                onChange={(e) => setContentPlanImportStartDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Content Plan Text</label>
              <textarea
                value={contentPlanImportText}
                onChange={(e) => setContentPlanImportText(e.target.value)}
                placeholder="Paste your content plan here. Day markers: ## DAY 1, DAY 1, or **Day 1: (Monday) - Title**

Example:
**Day 1: (Monday) - Addressing Trust**
Content Type: Image + Text
Text: Your post content here..."
                rows={15}
                style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.9rem',
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  minHeight: '300px',
                  resize: 'vertical',
                  lineHeight: '1.5'
                }}
              />
              <small style={{ 
                display: 'block',
                marginTop: '6px',
                fontSize: '0.85rem',
                color: '#6c757d',
                fontStyle: 'italic'
              }}>
                Supported day markers: "## DAY 1", "DAY 1", or "**Day 1: (Monday) - Title**". Paste your plan in the textarea above.
              </small>
            </div>

            {previewError && (
              <div className="alert alert-error">
                <strong>Parsing Error:</strong> {previewError}
              </div>
            )}

            {parsedPreview && !previewError && (
              <div className="content-plan-preview">
                <h4>Preview</h4>
                <p><strong>Campaign:</strong> {parsedPreview.campaignName}</p>
                <p><strong>Platform:</strong> {parsedPreview.platform.charAt(0).toUpperCase() + parsedPreview.platform.slice(1)}</p>
                <p><strong>Tasks Found:</strong> {parsedPreview.tasks.length} days</p>
                <p><strong>Duration:</strong> {parsedPreview.duration} days</p>
                <div className="preview-tasks-list" style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }}>
                  {parsedPreview.tasks.slice(0, 5).map((task, idx) => (
                    <div key={idx} style={{ padding: '5px', borderBottom: '1px solid #eee', fontSize: '0.85rem' }}>
                      <strong>Day {task.day}:</strong> {task.title}
                    </div>
                  ))}
                  {parsedPreview.tasks.length > 5 && (
                    <div style={{ padding: '5px', fontSize: '0.85rem', color: '#666' }}>
                      ... and {parsedPreview.tasks.length - 5} more tasks
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => setShowContentPlanImportModal(false)}>
              Cancel
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleImportContentPlan}
              disabled={!contentPlanImportText.trim() || !!previewError}
            >
              Parse & Create Campaign
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderContentPlanModal = () => (
    <div className="modal-overlay" onClick={() => setShowContentPlanModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Content Plan</h2>
          <button className="modal-close" onClick={() => setShowContentPlanModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Campaign Name</label>
            <input
              type="text"
              value={contentPlanData.campaignName}
              onChange={(e) => setContentPlanData({...contentPlanData, campaignName: e.target.value})}
              placeholder="Enter your campaign name (e.g., '15-Day Instagram Content Plan')"
            />
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={contentPlanData.description}
              onChange={(e) => setContentPlanData({...contentPlanData, description: e.target.value})}
              placeholder="Describe your content campaign goals and strategy"
              rows={3}
            />
          </div>
          
          <div className="form-group">
            <label>Platform</label>
            <select
              value={contentPlanData.platform}
              onChange={(e) => setContentPlanData({...contentPlanData, platform: e.target.value})}
            >
              <option value="multi-platform">Multi-Platform</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="youtube">YouTube</option>
              <option value="linkedin">LinkedIn</option>
              <option value="x">X (Twitter)</option>
              <option value="tiktok">TikTok</option>
              <option value="whatsapp-status">WhatsApp Status</option>
              <option value="blog">Blog</option>
              <option value="ecommerce">E-commerce</option>
              <option value="bluesky">Bluesky</option>
              <option value="website">Website</option>
              <option value="others">Others</option>
            </select>
          </div>
          
          <div className="form-group">
            <label>Duration (Days)</label>
            <select
              value={contentPlanData.duration}
              onChange={(e) => setContentPlanData({...contentPlanData, duration: parseInt(e.target.value)})}
            >
              <option value={7}>7 Days</option>
              <option value={15}>15 Days</option>
              <option value={30}>30 Days</option>
              <option value={60}>60 Days</option>
              <option value={90}>90 Days</option>
            </select>
          </div>

          <div className="content-plan-preview">
            <h4>Plan Preview</h4>
            <p><strong>Campaign:</strong> {contentPlanData.campaignName || 'Enter campaign name'}</p>
            <p><strong>Platform:</strong> {contentPlanData.platform.charAt(0).toUpperCase() + contentPlanData.platform.slice(1)}</p>
            <p><strong>Duration:</strong> {contentPlanData.duration} days</p>
            <p><strong>Description:</strong> {contentPlanData.description || 'Add your campaign description'}</p>
            <div className="content-plan-note">
              <p><strong>💡 Note:</strong> After creating your plan, you'll be able to add tasks and save your progress. Remember to click "Save Plan" to keep your changes!</p>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowContentPlanModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={createContentPlan}
            disabled={!contentPlanData.campaignName.trim()}
          >
            Create Content Plan
          </button>
        </div>
      </div>
    </div>
  );

  // Export work plans
  const handleExport = () => {
    try {
      // Get all tasks from localStorage
      const allTasks = [];
      selectedPlansForExport.forEach(planId => {
        const planTasks = JSON.parse(localStorage.getItem(`workPlanTasks_${planId}`) || '[]');
        allTasks.push(...planTasks);
      });

      // Get selected plans
      const plansToExport = plans.filter(plan => selectedPlansForExport.includes(plan.id));

      // Get templates if included
      const templatesToExport = includeTemplates ? userTemplates : [];

      const exportData = exportWorkPlans({
        plans: plansToExport,
        tasks: allTasks,
        templates: templatesToExport,
        selectedPlanIds: selectedPlansForExport,
      });

      downloadExportFile(exportData);
      setShowExportModal(false);
      alert('Work plans exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export work plans: ' + error.message);
    }
  };

  // Import work plans
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const importData = await readImportFile(file);
      
      // Import as standalone plans (always merge, but we call it 'merge' to generate new IDs)
      const imported = importWorkPlans(importData, 'merge');

      // Add imported plans alongside existing plans
      const updatedPlans = [...plans, ...imported.plans];
      setPlans(updatedPlans);
      localStorage.setItem('workPlans', JSON.stringify(updatedPlans));

      // Add imported tasks
      imported.tasks.forEach(task => {
        const existingTasks = JSON.parse(localStorage.getItem(`workPlanTasks_${task.planId}`) || '[]');
        existingTasks.push(task);
        localStorage.setItem(`workPlanTasks_${task.planId}`, JSON.stringify(existingTasks));
      });

      // Add imported templates
      if (imported.templates.length > 0) {
        const updatedTemplates = [...userTemplates, ...imported.templates];
        setUserTemplates(updatedTemplates);
        localStorage.setItem('userWorkPlanTemplates', JSON.stringify(updatedTemplates));
      }

      setShowImportModal(false);
      alert(`Successfully imported ${imported.plans.length} plan(s) and ${imported.tasks.length} task(s) as standalone plans!`);
    } catch (error) {
      console.error('Import error:', error);
      alert('Failed to import work plans: ' + error.message);
    }
  };

  const renderExportModal = () => (
    <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>📤 Export Work Plans</h2>
          <button className="modal-close" onClick={() => setShowExportModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="export-instructions">
            <p>Select which work plans to export. All tasks associated with selected plans will be included.</p>
          </div>
          
          <div className="export-options" style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              <input
                type="checkbox"
                checked={selectedPlansForExport.length === plans.length && plans.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedPlansForExport(plans.map(p => p.id));
                  } else {
                    setSelectedPlansForExport([]);
                  }
                }}
                style={{ marginRight: '8px' }}
              />
              Select All ({plans.length} plans)
            </label>
            {plans.map(plan => (
              <label key={plan.id} style={{ display: 'block', marginBottom: '8px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={selectedPlansForExport.includes(plan.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedPlansForExport([...selectedPlansForExport, plan.id]);
                    } else {
                      setSelectedPlansForExport(selectedPlansForExport.filter(id => id !== plan.id));
                    }
                  }}
                  style={{ marginRight: '8px' }}
                />
                {plan.name} ({JSON.parse(localStorage.getItem(`workPlanTasks_${plan.id}`) || '[]').length} tasks)
              </label>
            ))}
          </div>

          <div className="export-templates-option" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeTemplates}
                onChange={(e) => setIncludeTemplates(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Include templates ({userTemplates.length} templates)
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleExport}
            disabled={selectedPlansForExport.length === 0}
          >
            Export ({selectedPlansForExport.length} plan{selectedPlansForExport.length !== 1 ? 's' : ''})
          </button>
        </div>
      </div>
    </div>
  );

  const renderImportModal = () => (
    <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2>📥 Import Work Plans</h2>
          <button className="modal-close" onClick={() => setShowImportModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="import-instructions">
            <p>Select a JSON file exported from Work Plans to import.</p>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '10px' }}>
              <strong>Note:</strong> Imported plans will be added as standalone plans alongside your existing plans.
            </p>
          </div>
          
          <div className="file-upload-section" style={{ marginTop: '20px' }}>
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              className="file-input"
              id="import-file-input"
            />
            <label htmlFor="import-file-input" className="file-input-label" style={{ 
              display: 'block', 
              padding: '12px', 
              border: '2px dashed #ccc', 
              borderRadius: '8px', 
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: '#f8f9fa'
            }}>
              Click to select JSON file
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowImportModal(false)}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  const renderUploadModal = () => (
    <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Content Tasks</h2>
          <button className="modal-close" onClick={() => setShowUploadModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="upload-instructions">
            <h4>📋 Upload Instructions</h4>
            <p>Upload a CSV file with your content tasks. The file should include these columns:</p>
            <ul>
              <li><strong>Title</strong> - Task title</li>
              <li><strong>Content Type</strong> - Type of content (e.g., Carousel, Reel)</li>
              <li><strong>Content Idea</strong> - Full details of your content</li>
              <li><strong>Instruction</strong> - Additional AI instructions for content generation (optional)</li>
              <li><strong>Date</strong> - Due date (DD/MM/YYYY)</li>
            </ul>
            <p><em>Download the sample template first to see the correct format.</em></p>
          </div>
          
          <div className="file-upload-section">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="file-input"
            />
            <p className="file-hint">Select a CSV file to upload</p>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>
            Cancel
          </button>
          <button className="btn btn-info" onClick={downloadSampleTemplate}>
            📥 Download Template
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="workplan-container">
      {currentView === 'dashboard' && renderDashboard()}
      {currentView === 'filtered-tasks' && renderFilteredTasks()}
      {currentView === 'plan-detail' && selectedPlan && renderPlanDetail()}

      {showTemplateModal && renderTemplateModal()}
      {showSaveTemplateModal && renderSaveTemplateModal()}
      {showTaskParser && renderTaskParserModal()}
      {showContentPlanModal && renderContentPlanModal()}
      {showContentPlanImportModal && renderContentPlanImportModal()}
      {showUploadModal && renderUploadModal()}
      {showExportModal && renderExportModal()}
      {showImportModal && renderImportModal()}
    </div>
  );
};

export default WorkPlan;

